/**
 * SPRINT 4J: Multi-Vendor Cart Store
 * Zustand store for cart management with fractional weights
 */

import { create } from 'zustand';
import { cartApi } from '@/lib/api';
import { logger } from '@/lib/logger';

// ============================================================================
// TYPES
// ============================================================================

export interface CartItem {
  id: string;
  user_id: string;
  vendor_id: string;
  vendor_name: string;
  product_id: string;
  product_name: string;
  product_category: string | null;
  product_image_url: string | null;
  quantity: number;
  weight_kg: number | null;
  locked_price_per_unit: number;
  locked_gross_price: number;
  subtotal: number;
  zone_id: string | null;
  created_at: Date;
  updated_at: Date;
}

export interface VendorTotal {
  vendor_id: string;
  vendor_name: string;
  items: CartItem[];
  item_count: number;
  subtotal: number;
}

export interface DeliveryFeeBreakdown {
  base_fee: number;
  distance_km: number;
  weight_kg: number;
  extra_km: number;
  extra_km_fee: number;
  extra_weight: number;
  extra_weight_fee: number;
  total_fee: number;
  formula: string;
}

export interface CartSummary {
  items: CartItem[];
  vendor_totals: VendorTotal[];
  total_items: number;
  total_weight_kg: number;
  subtotal: number;
  platform_fee: number; // 12% commission (buyer pays)
  delivery_fee_breakdown: DeliveryFeeBreakdown | null;
  grand_total: number;
  zone_ids: string[];
  has_cross_zone_items: boolean;
}

interface CartStore {
  // State
  cart: CartSummary | null;
  isLoading: boolean;
  error: string | null;

  // Buyer location for delivery fee calculation
  buyerLocation: { lat: number; lng: number } | null;

  // Actions
  fetchCart: () => Promise<void>;
  addItem: (product_id: string, quantity: number) => Promise<void>;
  updateItem: (cart_item_id: string, quantity: number) => Promise<void>;
  removeItem: (cart_item_id: string) => Promise<void>;
  clearCart: () => Promise<void>;
  setBuyerLocation: (lat: number, lng: number) => void;
  validateCart: () => Promise<{ is_valid: boolean; errors: string[] }>;
}

// ============================================================================
// STORE
// ============================================================================

export const useCartStore = create<CartStore>((set, get) => ({
  // Initial state
  cart: null,
  isLoading: false,
  error: null,
  buyerLocation: null,

  // Fetch cart summary
  fetchCart: async () => {
    try {
      set({ isLoading: true, error: null });

      const { buyerLocation } = get();
      const cart = await cartApi.get(
        buyerLocation?.lat,
        buyerLocation?.lng
      );

      set({ cart, isLoading: false });
      logger.info('Cart fetched', { items: cart.total_items });
    } catch (error) {
      logger.error('Failed to fetch cart', error instanceof Error ? error : new Error(String(error)));
      set({
        error: error instanceof Error ? error.message : 'Failed to fetch cart',
        isLoading: false,
      });
    }
  },

  // Add item to cart
  addItem: async (product_id: string, quantity: number) => {
    try {
      set({ isLoading: true, error: null });

      await cartApi.addItem(product_id, quantity);

      // Refresh cart
      await get().fetchCart();

      logger.info('Item added to cart', { product_id, quantity });
    } catch (error) {
      logger.error('Failed to add item to cart', error instanceof Error ? error : new Error(String(error)));
      set({
        error: error instanceof Error ? error.message : 'Failed to add item',
        isLoading: false,
      });
      throw error;
    }
  },

  // Update cart item quantity
  updateItem: async (cart_item_id: string, quantity: number) => {
    try {
      set({ isLoading: true, error: null });

      await cartApi.updateItem(cart_item_id, quantity);

      // Refresh cart
      await get().fetchCart();

      logger.info('Cart item updated', { cart_item_id, quantity });
    } catch (error) {
      logger.error('Failed to update cart item', error instanceof Error ? error : new Error(String(error)));
      set({
        error: error instanceof Error ? error.message : 'Failed to update item',
        isLoading: false,
      });
      throw error;
    }
  },

  // Remove item from cart
  removeItem: async (cart_item_id: string) => {
    try {
      set({ isLoading: true, error: null });

      await cartApi.removeItem(cart_item_id);

      // Refresh cart
      await get().fetchCart();

      logger.info('Item removed from cart', { cart_item_id });
    } catch (error) {
      logger.error('Failed to remove cart item', error instanceof Error ? error : new Error(String(error)));
      set({
        error: error instanceof Error ? error.message : 'Failed to remove item',
        isLoading: false,
      });
      throw error;
    }
  },

  // Clear entire cart
  clearCart: async () => {
    try {
      set({ isLoading: true, error: null });

      await cartApi.clear();

      set({ cart: null, isLoading: false });

      logger.info('Cart cleared');
    } catch (error) {
      logger.error('Failed to clear cart', error instanceof Error ? error : new Error(String(error)));
      set({
        error: error instanceof Error ? error.message : 'Failed to clear cart',
        isLoading: false,
      });
      throw error;
    }
  },

  // Set buyer location for delivery fee calculation
  setBuyerLocation: (lat: number, lng: number) => {
    set({ buyerLocation: { lat, lng } });

    // Refresh cart to recalculate delivery fee
    get().fetchCart();
  },

  // Validate cart before checkout
  validateCart: async () => {
    try {
      const result = await cartApi.validate();
      return result;
    } catch (error) {
      logger.error('Cart validation failed', error instanceof Error ? error : new Error(String(error)));
      return {
        is_valid: false,
        errors: ['Cart validation failed. Please try again.'],
      };
    }
  },
}));
